'use client';

import { Search, Sparkles, User, HelpCircle, LogOut } from 'lucide-react';
import { colors, typography, radius } from '../../styles/design-tokens';
import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useNavbarProfile } from '../../hooks/useNavbarProfile';
import { SearchBar } from './SearchBar';
import { PromptBar } from './PromptBar';
import { TopNavbarSkeleton } from '../skeletons/TopNavbarSkeleton';
import { USER_ROLES } from '../../lib/role';
import { useRoleBasedUrl } from '../../hooks/useRoleBasedUrl';
import { APP_NAME } from '../../constants/brand/brandInfo';
import { nestLogoWithName } from '../../constants/brand/brandLogos';

export default function TopNavbar() {
  const [mounted, setMounted] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const { logout, user } = useAuth();
  const { addToast } = useToast();
  const { profile, isLoading: profileLoading } = useNavbarProfile();
  const { buildUrl } = useRoleBasedUrl();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (pendingSection && pathname === buildUrl('settings')) {
      window.dispatchEvent(
        new CustomEvent('setSettingsSection', {
          detail: { section: pendingSection },
        }),
      );
      setPendingSection(null);
    }
  }, [pathname, pendingSection]);

  const handleMenuItemClick = async (sectionId: string) => {
    setShowProfileMenu(false);

    if (sectionId === 'logout') {
      try {
        await logout();
      } catch (error) {
        console.error('Logout failed:', error);
        addToast('Logout failed. Please try again.', 'error');
      }
      return;
    }

    // Handle settings-related sections
    if (['profile', 'help'].includes(sectionId)) {
      // If user is admin and trying to access profile, redirect to settings
      if (sectionId === 'profile' && user?.role === USER_ROLES.ADMIN) {
        // Mark the intended settings subsection so the Settings page can react
        // to the navigation and open the correct profile section.
        setPendingSection('profile');
        router.push(buildUrl('settings?tab=general&section=profile'));
        return;
      }

      // For non-admin users or help section, use regular routes
      const routes: Record<string, string> = {
        profile: '/profile',
        help: '/help-and-support',
      };
      router.push(routes[sectionId]);
      return;
    }

    // Handle other settings sections
    router.push(buildUrl(`settings?section=${sectionId}`));
  };

  const profileMenuItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: <User className="w-4 h-4" />,
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: <HelpCircle className="w-4 h-4" />,
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: <LogOut className="w-4 h-4" />,
      className: 'text-red-500 hover:bg-red-50',
    },
  ];

  // Get user's first name with fallbacks
  const firstName = profile?.firstName || user?.firstName || 'User';

  // Show skeleton until profile data is loaded
  if (!mounted || profileLoading) return <TopNavbarSkeleton />;

  return (
    <>
      {/* Add a global CSS variable for the navbar height */}
      <style jsx global>{`
        :root {
          --top-navbar-height: 52px;
        }
      `}</style>

      <div className="w-full bg-white border-b border-[#E4E8EA] z-20 fixed top-0 left-0 right-0 h-[var(--top-navbar-height)]">
        <div className="max-w-[1920px] mx-auto px-6 relative h-full">
          <div className="flex items-center justify-between h-full gap-4">
            {/* Logo Section with PromptBar */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <img
                  src={nestLogoWithName.src}
                  alt={`${APP_NAME} Logo`}
                  height={24}
                  className="h-6 w-auto"
                />
              </div>
              <PromptBar />
            </div>

            {/* Right Side Elements */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-[#F5F8F9] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-[#E2FDCB] flex items-center justify-center">
                    {profile?.profileImage ? (
                      <img
                        src={profile.profileImage}
                        // alt={firstName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[#0D373D] text-sm font-medium">
                        {firstName ? firstName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-medium text-[#0D373D] max-w-[120px] truncate">
                      {firstName}
                    </div>
                  </div>
                </button>

                {showProfileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-[#E4E8EA] z-50">
                      <div className="py-1.5">
                        {profileMenuItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => handleMenuItemClick(item.id)}
                            className={`
                              flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm transition-colors
                              ${item.className || 'text-[#4D4F52] hover:bg-[#F5F8F9]'}
                            `}
                          >
                            {item.icon}
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add a spacer div to push content below the navbar */}
      <div className="h-[var(--top-navbar-height)] w-full"></div>
    </>
  );
}
